import { chartModes, impactColors, impactTypes } from './fields'
import { getFilteredReportData } from '../helpers/filterHelpers'
import {
  CATEGORICAL_IMPACT,
  HEALTH_IMPACT,
  INDIRECT_FINANCIAL_HEALTH_IMPACT,
  INDIRECT_FINANCIAL_HUMAN_CAPITAL_IMPACT,
  SPORTS_TYPE,
  STAKEHOLDER_TYPE,
  TOTAL_IMPACT,
  WELLBEING_IMPACT
} from '../fields'

// Helper functions for generating Social Value by Stakeholders chart data

// Format snake_case string to Title Case
const formatLabel = str => {
  if (!str) return ''
  return str
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

// Extract labels from categorical_impact data
export const getCategoricalLabels = categoricalImpact => {
  if (categoricalImpact?.length) {
    return categoricalImpact.map(item => {
      const sportsType = item[SPORTS_TYPE]
      const stakeholderType = item[STAKEHOLDER_TYPE]

      const formattedSportType = formatLabel(sportsType)
      const formattedStakeholderType = formatLabel(stakeholderType)

      return `${formattedSportType} ${formattedStakeholderType}`
    })
  } else {
    return []
  }
}

// Get data for specific field from categorical_impact
const getCategoricalDataForField = (categoricalImpact, fieldName) => {
  if (categoricalImpact?.length) {
    return categoricalImpact.map(item => {
      return item[fieldName] || 0
    })
  } else {
    return []
  }
}

// Generate chart data with 4 impact pathway components (stacked bars)
const generateCategoricalChartData = categoricalImpact => {
  const labels = getCategoricalLabels(categoricalImpact)

  return {
    labels,

    datasets: [
      {
        label: 'Health',
        data: getCategoricalDataForField(categoricalImpact, impactTypes[HEALTH_IMPACT]),
        backgroundColor: impactColors[HEALTH_IMPACT],
        borderSkipped: false,
        maxBarThickness: 45
      },

      {
        label: 'Wellbeing',
        data: getCategoricalDataForField(categoricalImpact, impactTypes[WELLBEING_IMPACT]),
        backgroundColor: impactColors[WELLBEING_IMPACT],
        borderSkipped: false,
        maxBarThickness: 45
      },

      {
        label: 'Indirect financial via health',
        data: getCategoricalDataForField(categoricalImpact, impactTypes[INDIRECT_FINANCIAL_HEALTH_IMPACT]),
        backgroundColor: impactColors[INDIRECT_FINANCIAL_HEALTH_IMPACT],
        borderSkipped: false,
        maxBarThickness: 45
      },
      {
        label: 'Indirect financial via human capital',
        data: getCategoricalDataForField(categoricalImpact, impactTypes[INDIRECT_FINANCIAL_HUMAN_CAPITAL_IMPACT]),
        backgroundColor: impactColors[INDIRECT_FINANCIAL_HUMAN_CAPITAL_IMPACT],
        borderSkipped: false,
        maxBarThickness: 45,
        borderRadius: {
          topLeft: 4,
          topRight: 4,
          bottomLeft: 0,
          bottomRight: 0
        }
      }
    ]
  }
}

// Generate chart data with total impact (single bars)
const generateTotalChartData = categoricalImpact => {
  const labels = getCategoricalLabels(categoricalImpact)

  return {
    labels,

    datasets: [
      {
        label: 'Total Impact',
        data: getCategoricalDataForField(categoricalImpact, TOTAL_IMPACT),
        backgroundColor: '#D08B67',
        borderSkipped: false,
        maxBarThickness: 45,
        borderRadius: 4
      }
    ]
  }
}

// Main function to generate chart data based on mode and filters
export const generateSocialValueByStakeholdersData = ({ chartMode, selectedRegion, report }) => {
  // Get categorical data with region filtering applied (if region is selected)
  // Note: skipStakeholderFilter is true because this chart doesn't use stakeholder filter
  const categoricalData = getFilteredReportData({
    report,
    selectedRegion,
    selectedStakeholder: null,
    fieldName: CATEGORICAL_IMPACT,
    skipStakeholderFilter: true
  })

  // Generate chart based on selected tab mode
  if (chartMode === chartModes.byImpactPathway) {
    // 'By Impact Pathway' tab - stacked bars with 4 segments
    return generateCategoricalChartData(categoricalData)
  } else {
    // 'Total' tab - single bars showing total_impact
    return generateTotalChartData(categoricalData)
  }
}
